<template>
  <div id="mytask-layout" class="theme-indigo wx-shell">
    <!-- Icon rail (bottom bar on mobile) -->
    <nav id="main-menu" class="wx-rail" aria-label="Κύριο μενού">
      <RouterLink :to="homePath" class="wx-logo" title="Worxspace">W</RouterLink>

      <template v-for="mod in railModules" :key="mod.key">
        <RouterLink v-if="mod.accessible" :to="mod.to" class="wx-ri" :class="{ on: currentModule && currentModule.key === mod.key }">
          <WxIcon :name="mod.icon" />
          <span class="wx-tip">{{ mod.label }}</span>
        </RouterLink>
        <a v-else href="#" class="wx-ri locked" @click.prevent="showAccessDenied">
          <WxIcon :name="mod.icon" />
          <span class="wx-tip">{{ mod.label }} · ανενεργό</span>
        </a>
      </template>

      <div class="wx-grow"></div>

      <button type="button" class="wx-ri wx-desk" @click="toggleTheme" :title="isDark ? 'Light mode' : 'Dark mode'">
        <WxIcon :name="isDark ? 'sun' : 'moon'" />
        <span class="wx-tip">{{ isDark ? 'Light mode' : 'Dark mode' }}</span>
      </button>

      <div class="wx-pop-anchor notifications">
        <a href="#" class="wx-ri notifications-menu-toggler" :class="{ on: isNotificationsOpen }" @click.prevent="toggleNotificationMenu()">
          <WxIcon name="bell" />
          <span v-if="showNotificationBadge" id="notificationBadge" class="wx-badge">{{ notificationCount }}</span>
          <span class="wx-tip">Notifications</span>
        </a>
        <div v-show="isNotificationsOpen" class="wx-pop notifications-menu">
          <div class="wx-pop-head">
            <b>Notifications</b>
            <span class="wx-count" id="notificationsCount"></span>
          </div>
          <ul class="list-unstyled mb-0 wx-notes" id="notifications-list"></ul>
          <RouterLink class="wx-pop-foot" to="/notifications" @click="isNotificationsOpen = false">All Notifications →</RouterLink>
        </div>
      </div>

      <div class="wx-pop-anchor user-profile">
        <a href="#" class="wx-me user-menu-toggler" @click.prevent="toggleMenu()" :title="userInfo ? userInfo.username : ''">
          <img v-if="hasAvatar" :src="avatarUrl" alt="">
          <span v-else>{{ initials }}</span>
        </a>
        <div v-show="isMenuOpen" class="wx-pop user-menu">
          <div class="wx-pop-user">
            <span class="wx-me lg"><img v-if="hasAvatar" :src="avatarUrl" alt=""><span v-else>{{ initials }}</span></span>
            <div class="wx-min">
              <b class="user-name" v-if="userInfo">{{ userInfo.username }}</b>
              <small class="user-email" v-if="userInfo">{{ userInfo.email }}</small>
            </div>
          </div>
          <div class="users-container" v-if="showUsersContainer">
            <label class="form-label" for="user">Αλλαγή πελάτη</label>
            <select class="form-select" name="user" id="user" aria-label="Πελάτης" v-model="selectedUser"
              @change="changeUserHandler(selectedUser)">
              <option value="" selected>Παρακαλώ επιλέξτε</option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.username }}</option>
            </select>
          </div>
          <RouterLink class="wx-mi" to="/change-profile" @click="isMenuOpen = false"><WxIcon name="user" :size="18" />Επεξεργασία λογαριασμού</RouterLink>
          <RouterLink class="wx-mi" to="/my-permissions" v-if="!isAccountant" @click="isMenuOpen = false"><WxIcon name="lock" :size="18" />Τα δικαιώματά μου</RouterLink>
          <RouterLink class="wx-mi" to="/notification-settings" v-if="hasProjectManagement && !isAccountant" @click="isMenuOpen = false"><WxIcon name="settings" :size="18" />Ρυθμίσεις ειδοποιήσεων</RouterLink>
          <button type="button" class="wx-mi wx-mob" @click="toggleTheme"><WxIcon :name="isDark ? 'sun' : 'moon'" :size="18" />{{ isDark ? 'Light mode' : 'Dark mode' }}</button>
          <a href="#" class="wx-mi danger btn-logout" @click.prevent="logout"><WxIcon name="logout" :size="18" />Αποσύνδεση</a>
        </div>
      </div>
    </nav>

    <!-- Main sheet -->
    <div class="main wx-main">
      <header id="main-header" class="wx-bar">
        <button type="button" class="wx-back" @click="$router.go(-1)" title="Πίσω"><WxIcon name="back" :size="18" /></button>
        <h1 class="page-header wx-title">{{ pageHeader }}</h1>
        <p class="wx-sub" v-if="userInfo && pageHeader == 'Days Off'">Διαθέσιμες ημέρες: <b>{{ userInfo.remaining_days_off }}</b></p>
        <div class="wx-grow"></div>
        <nav class="wx-subnav" v-if="subPages.length > 1" aria-label="Υπομενού">
          <RouterLink v-for="page in subPages" :key="page.to" :to="page.to" :class="{ on: page.active }">{{ page.label }}</RouterLink>
        </nav>
        <RouterLink v-if="hasProjectManagement && !isAccountant && route.path !== '/hours'" to="/hours" class="wx-logtime">
          <WxIcon name="clock" :size="17" /><span>Log time</span>
        </RouterLink>
      </header>

      <main class="container-xxl wx-content" id="main-container">
        <LoadingSpinner :isLoading="isLoading" />
        <slot></slot>
      </main>

      <MainFooter />
    </div>

    <div v-if="isMenuOpen || isNotificationsOpen" class="wx-scrim" @click="isMenuOpen = false; isNotificationsOpen = false"></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { fetchUsers, changeUser, getAllUnreadNotifications } from '../services/user';
import { fetchUserInfo } from '@/services/userInfo';
import { useToast } from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import WxIcon from '../components/WxIcon.vue';
import { mapActions, mapGetters } from 'vuex';
import MainFooter from './MainFooter.vue';
import { modules, accountModule, accountantModule, moduleForPath, pageIsActive } from '../nav';

export default {
  name: 'MainLayout',
  components: {
    LoadingSpinner, MainFooter, WxIcon
  },
  mounted() {
    this.fetchUserData();
  },
  methods: {
    ...mapActions(['fetchUserData']),
    goBack() {
      window.history.back();
    },
    canSeePage(page) {
      if (page.super && !this.superUser) return false;
      if (page.notAccountant && this.isAccountant) return false;
      if (page.packet && !this.getUserPackets.includes(page.packet)) return false;
      if (page.perm && !this.superUser && this.getUserPermission(page.perm)?.view !== 1) return false;
      return true;
    },
  },
  computed: {
    ...mapGetters({
      getUserPermission: 'getUserPermission',
      getSuperUserPermission: 'getSuperUserPermission',
      getIsAccountant: 'getIsAccountant',
      getUserPackets: 'getUserPackets'
    }),
    canViewDashboard() {
      return this.getUserPermission('Dashboard')?.view === 1;
    },
    superUser() {
      return this.getSuperUserPermission === 1;
    },
    isAccountant() {
      return this.getIsAccountant === 1;
    },
    hasProjectManagement() {
      return this.getUserPackets.includes('PROJECT MANAGEMENT');
    },
    homePath() {
      if (this.isAccountant) return '/accountant-dashboard';
      if (!this.hasProjectManagement) return '/users';
      return (this.superUser || this.canViewDashboard) ? '/dashboard' : '/projects';
    },
    // Same order as the old sidebar: modules the customer has first, locked ones after.
    railModules() {
      if (this.isAccountant) {
        return [
          { ...accountantModule, to: '/accountant-dashboard', accessible: true },
          { key: 'notes', label: 'Notifications', icon: 'bell', to: '/notifications', accessible: true, hidden: true },
        ].filter((m) => !m.hidden);
      }
      const list = modules.map((mod) => ({
        ...mod,
        accessible: mod.key === 'dash' ? true : (!mod.packet || this.getUserPackets.includes(mod.packet)),
      }));
      return [...list.filter((m) => m.accessible), ...list.filter((m) => !m.accessible)];
    },
    currentModule() {
      const pool = this.isAccountant ? [accountantModule, accountModule] : [...modules, accountModule];
      return moduleForPath(this.route.path, pool);
    },
    subPages() {
      if (!this.currentModule) return [];
      return this.currentModule.pages
        .filter((page) => this.canSeePage(page))
        .map((page) => ({ ...page, active: pageIsActive(page, this.route.path) }));
    },
  },
  setup() {
    const isLoading = ref(false);
    const userInfo = ref(null);
    const users = ref([]);
    const selectedUser = ref(null);
    const showUsersContainer = ref(true);
    const toast = useToast();
    const isMenuOpen = ref(false);
    const isNotificationsOpen = ref(false);
    const router = useRouter();
    const route = useRoute();
    const avatarUrl = ref('');
    const hasAvatar = ref(false);

    const pageHeader = ref(null);
    const notificationCount = ref(0);
    const isFetchingNotifications = ref(false);

    const isDark = ref(localStorage.getItem('theme') === 'dark');

    const initials = computed(() => {
      const name = userInfo.value?.username || '';
      return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || '·';
    });

    const showNotificationBadge = computed(() => notificationCount.value > 0);

    const showAccessDenied = () => {
      toast.error('Δεν έχετε ενεργοποιήσει τη συγκεκριμένη λειτουργία!!!');
    };

    const fetchNotificationCount = async () => {
      try {
        isFetchingNotifications.value = true;
        const response = await getAllUnreadNotifications();
        isLoading.value = false;
        if (response && response.data && response.data.data) {
          notificationCount.value = response.data.data.length;
        } else {
          console.error("Error in response format or no data received.");
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        isFetchingNotifications.value = false;
      }
    };

    watch(
      () => route.fullPath,
      (newPath, oldPath) => {
        fixPageHeader(newPath, oldPath);
        isMenuOpen.value = false;
        isNotificationsOpen.value = false;
      });

    const startLoading = () => {
      isLoading.value = true;
    };

    const stopLoading = () => {
      isLoading.value = false;
    };

    const toggleMenu = () => {
      isMenuOpen.value = !isMenuOpen.value;
      isNotificationsOpen.value = false;
    };

    const getMentionType = (notificationType) => {
      switch (notificationType) {
        case 'DAYS_OFF_REQUEST_ACCEPTED':
          return 'DAYS OFF ▶ ACCEPTED';
        case 'DAYS_OFF_REQUEST_CREATION':
          return 'DAYS OFF ▶ CREATION';
        case 'DAYS_OFF_REQUEST_REJECTED':
          return 'DAYS OFF ▶ REJECTED';
        case 'HOURS_REQUEST_ACCEPTED':
          return 'HOURS ▶ ACCEPTED';
        case 'HOURS_REQUEST_CREATION':
          return 'HOURS ▶ CREATION';
        case 'HOURS_REQUEST_REJECTED':
          return 'HOURS ▶ REJECTED';
        case 'MANAGER_ASSIGNMENT_PROJECT':
          return 'ASSIGNMENT ▶ PROJECT MANAGER';
        case 'MANAGER_ASSIGNMENT_SUBTASK':
          return 'ASSIGNMENT ▶ SUBTASK MANAGER';
        case 'MANAGER_ASSIGNMENT_TASK':
          return 'ASSIGNMENT ▶ TASK MANAGER';
        case 'PROJECT_ASSIGNMENT':
          return 'ASSIGNMENT ▶ PROJECT';
        case 'PROJECT_COMMENT_MENTION':
          return 'PROJECT ▶ MENTION';
        case 'PROJECT_CREATOR_STATUS_CHANGES':
          return 'PROJECT ▶ STATUS CHANGE';
        case 'PROJECT_MANAGER_EDIT':
          return 'PROJECT ▶ CHANGE';
        case 'PROJECT_MANAGER_STATUS_CHANGES':
          return 'PROJECT ▶ STATUS CHANGE';
        case 'SUBTASK_ASSIGNMENT':
          return 'ASSIGNMENT ▶ SUBTASK';
        case 'SUBTASK_COMMENT_MENTION':
          return 'SUBTASK ▶ MENTION';
        case 'SUBTASK_CREATOR_STATUS_CHANGES':
          return 'SUBTASK ▶ STATUS CHANGE';
        case 'SUBTASK_MANAGER_EDIT':
          return 'SUBTASK ▶ CHANGE';
        case 'SUBTASK_MANAGER_STATUS_CHANGES':
          return 'SUBTASK ▶ STATUS CHANGE';
        case 'TASK_ASSIGNMENT':
          return 'ASSIGNMENT ▶ TASK';
        case 'TASK_COMMENT_MENTION':
          return 'TASK ▶ MENTION';
        case 'TASK_CREATOR_STATUS_CHANGES':
          return 'TASK ▶ STATUS CHANGE';
        case 'TASK_MANAGER_EDIT':
          return 'TASK ▶ CHANGE';
        case 'TASK_MANAGER_STATUS_CHANGES':
          return 'TASK ▶ STATUS CHANGE';
        case 'MISSING_HOURS_DAYS':
          return 'HOURS ▶ MISSING';
        case 'POTENTIAL_CLIENT_REMINDER':
          return 'CLIENT ▶ POTENTIAL REMINDER';
        case 'POTENTIAL_PROJECT_REMINDER':
          return 'PROJECT ▶ POTENTIAL REMINDER';
        case 'WRONG_HOSTING_EXPIRATION_DATA':
          return 'HOSTING ▶ WRONG EXPIRATION DATA';
        case 'HOSTING_WILL_EXPIRE':
          return 'HOSTING ▶ WILL EXPIRE';
        case 'HOSTING_EXPIRED':
          return 'HOSTING ▶ EXPIRED';
        case 'EXPIRED_PROJECT_REMINDER':
          return 'PROJECT ▶ EXPIRED';
        case 'EXPIRED_TASK_REMINDER':
          return 'TASK ▶ EXPIRED';
        case 'EXPIRED_SUBTASK_REMINDER':
          return 'SUBTASK ▶ EXPIRED';
        case 'IS_ABOUT_TO_EXPIRE_PROJECT_REMINDER':
          return 'PROJECT ▶ EXPIRES SOON';
        case 'IS_ABOUT_TO_EXPIRE_TASK_REMINDER':
          return 'TASK ▶ EXPIRES SOON';
        case 'IS_ABOUT_TO_EXPIRE_SUBTASK_REMINDER':
          return 'SUBTASK ▶ EXPIRES SOON';
        case 'NEW_EMPLOYEE_SUBMIT_FORM':
          return 'FORM ▶ SUBMISSION';
        default:
          return '-';
      }
    };

    const handleNotificationLinkClick = () => {
      window.location.reload();
    };

    const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

    const getMentionTypeLink = (notification) => {
      let ret = '';

      if (notification.link) {
        // Remove domain from link (keep only the path and query)
        const trimmedLink = new URL(notification.link).pathname + new URL(notification.link).search;

        const title =
          notification.subtask_title ||
          notification.task_title ||
          notification.project_title ||
          notification.description ||
          'View Notification';

        ret = `<a href="${escapeHtml(trimmedLink)}" class="notification-link">${escapeHtml(title)}</a>`;
      }

      if (notification.username) {
        ret += ` <span class="wx-from">από ${escapeHtml(notification.username)}</span>`;
      }

      return ret;
    };

    const toggleNotificationMenu = async () => {
      isNotificationsOpen.value = !isNotificationsOpen.value;
      isMenuOpen.value = false;

      if (isNotificationsOpen.value) {
        try {
          const response = await getAllUnreadNotifications();

          if (response && response.data) {
            const notifications = response.data.data;
            if (Array.isArray(notifications)) {
              notificationCount.value = notifications.length;
              const notificationsList = document.getElementById('notifications-list');
              notificationsList.innerHTML = '';
              document.getElementById('notificationsCount').textContent = notifications.length;

              if (!notifications.length) {
                notificationsList.innerHTML = '<li class="wx-empty">Δεν υπάρχουν νέες ειδοποιήσεις.</li>';
              }

              notifications.forEach(notification => {
                const listItem = document.createElement('li');

                const type = document.createElement('span');
                type.className = 'wx-type';
                type.textContent = getMentionType(notification.notificationType);
                listItem.appendChild(type);

                const notificationMessage = document.createElement('p');
                notificationMessage.className = 'notification-message';
                notificationMessage.innerHTML = getMentionTypeLink(notification);
                listItem.appendChild(notificationMessage);

                const timestamp = document.createElement('span');
                timestamp.className = 'wx-time';
                timestamp.textContent = notification.created_at;
                listItem.appendChild(timestamp);

                notificationsList.appendChild(listItem);
              });
            } else {
              console.error("Notifications data is not in the expected format:", notifications);
            }
          } else {
            console.error("Response data is missing:", response);
          }
        } catch (error) {
          console.error("Failed to fetch notifications:", error);
        }
      }
    };

    const fetchUserData = async () => {
      try {
        const response = await fetchUserInfo();
        userInfo.value = response.data?.info || null;
        if (response.data?.info?.avatar_file_name) {
          avatarUrl.value = 'https://mis.worxpace.gr/storage/avatars/' + response.data.info.avatar_file_name;
          hasAvatar.value = true;
        } else {
          hasAvatar.value = false;
        }
        if (userInfo.value.customer_id === null && userInfo.value.superUser === 1) {
          showUsersContainer.value = true;
          fetchUsersData();
        } else {
          showUsersContainer.value = false;
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    const fetchUsersData = async () => {
      try {
        const response = await fetchUsers();
        showUsersContainer.value = response.data.success;
        if (response.data.success) {
          users.value = response.data.data;
        } else {
          console.error("Failed to fetch users:", response.data.message);
        }
      } catch (error) {
        showUsersContainer.value = false;
        console.error("Failed to fetch users:", error);
      }
    };

    // Handle user change
    const changeUserHandler = async (userId) => {
      const userInfo = await fetchUserInfo();
      if (userInfo.data.info?.id) {
        if (userId && parseInt(userInfo.data.info?.id) != parseInt(userId)) {
          try {
            const response = await changeUser(userId);
            if (response.data.success) {
              const newToken = response.data.data.token.plainTextToken;

              localStorage.removeItem('api_token');
              localStorage.setItem('api_token', newToken);

              await fetchUserData();

              toast.success(response.data.message);
              window.location.reload();
            } else {
              toast.error(response.data.message);
            }
          } catch (error) {
            console.error('Error changing user:', error);
            toast.error('An error occurred while changing the user.');
          }
        }
      }
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        isMenuOpen.value = false;
        isNotificationsOpen.value = false;
      }
    };

    const logout = (event) => {
      event.preventDefault();
      localStorage.removeItem('api_token');
      localStorage.removeItem('main_auth_token');
      window.location.reload();
    };

    const applyTheme = () => {
      document.documentElement.setAttribute('data-theme', isDark.value ? 'dark' : 'light');
    };

    const toggleTheme = () => {
      isDark.value = !isDark.value;
      localStorage.setItem('theme', isDark.value ? 'dark' : 'light');
      applyTheme();
    };

    const headers = [
      ['/dashboard', 'Dashboard'],
      ['/edit-dashboard', 'Dashboard widgets'],
      ['/follow-up', 'Follow Up'],
      ['/to-do', 'To Do'],
      ['/missing-hours', 'Missing Hours'],
      ['/users', 'Users'],
      ['/employees', 'Εργαζόμενοι'],
      ['/employees-days-off', 'Εργαζόμενοι'],
      ['/employee-profile', 'Προφίλ Εργαζόμενου'],
      ['/crm-menu', 'CRM'],
      ['/hr-menu', 'HR'],
      ['/pm-menu', 'Project Management'],
      ['/marketing-hub-menu', 'Marketing Hub'],
      ['/reporting-analytics-menu', 'Reporting & Analytics'],
      ['/file-management-menu', 'File Management'],
      ['/med-menu', 'MED'],
      ['/project', 'Project'],
      ['/project-report', 'Project Report'],
      ['/host', 'Hosting'],
      ['/projects', 'Projects'],
      ['/client', 'Client Profile'],
      ['/supplier', 'Supplier Profile'],
      ['/hosting', 'Hosting'],
      ['/services', 'Services'],
      ['/service-categories', 'Service Categories'],
      ['/clients', 'Clients'],
      ['/suppliers', 'Suppliers'],
      ['/contacts', 'Contacts'],
      ['/contacts2', 'Contacts'],
      ['/contacts-clients', 'Contacts Clients'],
      ['/contacts-suppliers', 'Contacts Suppliers'],
      ['/contact', 'Contact Profile'],
      ['/orphaned-contacts', 'Orphaned Contacts'],
      ['/days-off', 'Days Off'],
      ['/days-off-reports', 'Days Off Reports'],
      ['/days-off-requests', 'Days Off Requests'],
      ['/other-requests', 'Other Requests'],
      ['/holidays', 'Holidays'],
      ['/reports', 'Reports'],
      ['/hours', 'Hours'],
      ['/hours-requests', 'Hours Requests'],
      ['/history-changes', 'Ιστορικό αλλαγών σε clients'],
      ['/notifications', 'Notifications'],
      ['/change-profile', 'Επεξεργασία Λογαριασμού'],
      ['/my-permissions', 'My permissions'],
      ['/notification-settings', 'Notification Settings'],
      ['/box-integration', 'Box Integration'],
      ['/task', 'Task'],
      ['/task-report', 'Task Report'],
      ['/subtask', 'Subtask'],
      ['/edit-list', 'Edit List'],
      ['/lists', 'Lists'],
      ['/list-profile', 'List Profile'],
      ['/accountant-dashboard', 'Εταιρείες'],
      ['/under-recruitment', 'Για πρόσληψη'],
      ['/under-recruitment-profile', 'Για πρόσληψη'],
    ];

    const fixPageHeader = () => {
      const path = route.path.replace(/\/+$/, '') || '/';
      const found = headers.find(([p]) => p === path);
      pageHeader.value = found ? found[1] : (route.name || '');
    };

    onMounted(async () => {
      fixPageHeader();
      applyTheme();

      if (!userInfo.value) {
        fetchUserData();
      }

      $(document.body).on('click', '.notification-link', handleNotificationLinkClick);
      document.addEventListener('keydown', onKeydown);

      router.beforeEach((to, from, next) => {
        startLoading();
        next();
      });

      router.afterEach(() => {
        stopLoading();
      });

      fetchNotificationCount();
    });

    onUnmounted(async () => {
      $(document.body).off('click', '.notification-link', handleNotificationLinkClick);
      document.removeEventListener('keydown', onKeydown);
    });

    return {
      route,
      userInfo,
      users,
      selectedUser,
      toggleMenu,
      isMenuOpen,
      isNotificationsOpen,
      toggleNotificationMenu,
      notificationCount,
      showUsersContainer,
      changeUserHandler,
      isDark,
      toggleTheme,
      isLoading,
      logout,
      avatarUrl,
      hasAvatar,
      initials,
      pageHeader,
      showNotificationBadge,
      showAccessDenied
    };
  }
};
</script>

<style>
/* ---------- shell ---------- */
.wx-shell {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 14px;
  padding: 14px;
  min-height: 100vh;
}
.wx-rail {
  background: var(--wx-rail);
  border-radius: 24px;
  padding: 14px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  position: sticky;
  top: 14px;
  height: calc(100vh - 28px);
  z-index: 1040;
}
.wx-logo {
  width: 42px; height: 42px; border-radius: 14px; background: var(--wx-g-proj);
  display: grid; place-items: center; color: #fff !important; font-weight: 800; font-size: 17px;
  margin-bottom: 14px; box-shadow: 0 6px 18px rgba(109, 93, 252, .5); text-decoration: none;
}
.wx-ri {
  width: 46px; height: 46px; border-radius: 14px; display: grid; place-items: center;
  color: var(--wx-rail-txt) !important; position: relative; transition: background .15s, color .15s;
  background: none; border: 0; text-decoration: none; flex: none;
}
.wx-ri:hover { background: var(--wx-rail-2); color: #fff !important; }
.wx-ri.on, .wx-ri.router-link-active.on { background: #fff; color: var(--wx-rail) !important; }
.wx-ri.locked { opacity: .35; }
.wx-tip {
  position: absolute; left: 58px; background: var(--wx-rail); color: #fff; font-size: 12px; font-weight: 500;
  padding: 5px 10px; border-radius: 8px; white-space: nowrap; opacity: 0; pointer-events: none; transition: opacity .15s; z-index: 40;
}
.wx-ri:hover .wx-tip { opacity: 1; }
.wx-badge {
  position: absolute; top: 5px; right: 4px; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 99px;
  background: #ff6b8b; color: #fff; font-size: 10px; font-weight: 700; display: grid; place-items: center; border: 2px solid var(--wx-rail);
}
.wx-grow { flex: 1; }
.wx-me {
  width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #38bdf8, #6366f1);
  display: grid; place-items: center; color: #fff !important; font-size: 13px; font-weight: 700;
  border: 2px solid var(--wx-rail-2); overflow: hidden; text-decoration: none; flex: none; margin-top: 6px;
}
.wx-me img { width: 100%; height: 100%; object-fit: cover; }
.wx-me.lg { width: 46px; height: 46px; margin: 0; border: 0; }

/* popovers (notifications, user menu) open beside the rail */
.wx-pop-anchor { position: relative; }
.wx-pop {
  position: absolute; left: 62px; bottom: 0; width: 340px; max-height: min(560px, calc(100vh - 40px));
  overflow: auto; background: var(--wx-card); color: var(--wx-text); border-radius: 20px;
  box-shadow: var(--wx-sh-lg); padding: 10px; z-index: 1060;
}
.wx-pop-head { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px 10px; font-size: 15px; }
.wx-count { background: var(--wx-accent-soft); color: var(--wx-accent); font-weight: 700; font-size: 12px; padding: 2px 8px; border-radius: 99px; }
.wx-count:empty { display: none; }
.wx-notes li { padding: 10px 12px; border-radius: 14px; margin-bottom: 4px; background: var(--wx-soft); }
.wx-notes li.wx-empty { background: none; color: var(--wx-text-3); text-align: center; padding: 24px; }
.wx-notes .wx-type { font-size: 10.5px; font-weight: 700; letter-spacing: .04em; color: var(--wx-accent); }
.wx-notes .notification-message { margin: 2px 0; font-size: 13px; color: var(--wx-text); }
.wx-notes .notification-link { color: var(--wx-text) !important; font-weight: 650; text-decoration: none; }
.wx-notes .wx-from { color: var(--wx-text-2); }
.wx-notes .wx-time { font-size: 11.5px; color: var(--wx-text-3); }
.wx-pop-foot { display: block; text-align: center; padding: 10px; font-weight: 600; font-size: 13px; color: var(--wx-accent) !important; text-decoration: none; }
.wx-pop-user { display: flex; align-items: center; gap: 12px; padding: 8px 8px 14px; }
.wx-pop-user b { display: block; font-size: 14px; }
.wx-pop-user small { color: var(--wx-text-3); font-size: 12px; }
.wx-min { min-width: 0; }
.wx-pop .users-container { padding: 0 8px 10px; }
.wx-mi {
  display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px; border-radius: 12px; border: 0; background: none;
  color: var(--wx-text) !important; font-size: 13.5px; font-weight: 500; text-decoration: none; text-align: left;
}
.wx-mi:hover { background: var(--wx-soft); }
.wx-mi.danger { color: var(--wx-late) !important; }
.wx-scrim { position: fixed; inset: 0; z-index: 1030; }

/* ---------- main sheet ---------- */
.wx-main {
  background: var(--wx-sheet);
  /* no backdrop-filter here: it would trap Bootstrap modals under their backdrop (stacking context) */
  border-radius: 28px;
  box-shadow: var(--wx-sh);
  min-width: 0;
  padding: 18px 26px 26px !important;
  display: flex;
  flex-direction: column;
}
.wx-bar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 18px;
  background: transparent !important; position: static; z-index: 2;
}
.wx-title { font-size: 26px !important; font-weight: 800 !important; letter-spacing: -.035em; margin: 0; color: var(--wx-text); }
.wx-sub { margin: 0; font-size: 13px; color: var(--wx-text-2); }
.wx-back {
  width: 36px; height: 36px; border-radius: 12px; border: 0; background: var(--wx-soft); color: var(--wx-text-2);
  display: none; place-items: center;
}
.wx-subnav { display: flex; gap: 4px; background: var(--wx-soft); padding: 4px; border-radius: 14px; flex-wrap: wrap; }
.wx-subnav a {
  padding: 7px 13px; border-radius: 10px; font-weight: 600; font-size: 13px; color: var(--wx-text-2) !important;
  white-space: nowrap; text-decoration: none;
}
.wx-subnav a:hover { color: var(--wx-text) !important; }
.wx-subnav a.on { background: var(--wx-card); color: var(--wx-text) !important; box-shadow: var(--wx-sh); }
.wx-logtime {
  display: inline-flex; align-items: center; gap: 7px; height: 40px; padding: 0 16px; border-radius: 14px;
  background: var(--wx-text); color: var(--wx-card) !important; font-weight: 600; font-size: 13px; text-decoration: none;
}
.wx-content { padding: 0 !important; max-width: none !important; flex: 1; }
.wx-content > .body { padding-top: 0 !important; }
.wx-content .container-xxl { max-width: none; padding-left: 0; padding-right: 0; }

/* ---------- mobile: rail turns into a bottom bar ---------- */
.wx-mob { display: none; }
@media (max-width: 860px) {
  .wx-shell { grid-template-columns: minmax(0, 1fr); padding: 8px 8px 88px; gap: 0; }
  .wx-rail {
    position: fixed; top: auto; bottom: 8px; left: 8px; right: 8px; height: 64px; flex-direction: row;
    justify-content: space-around; padding: 0 6px; border-radius: 22px; gap: 0; overflow-x: auto;
  }
  .wx-logo, .wx-grow, .wx-desk, .wx-tip { display: none !important; }
  .wx-ri { width: 42px; height: 42px; }
  .wx-ri.locked { display: none; }
  .wx-me { margin: 0; width: 36px; height: 36px; }
  .wx-pop { position: fixed; left: 8px; right: 8px; bottom: 80px; width: auto; }
  .wx-pop-anchor { position: static; }
  .wx-mob { display: flex; }
  .wx-main { padding: 14px 14px 20px !important; border-radius: 22px; }
  .wx-back { display: grid; }
  .wx-title { font-size: 21px !important; }
  .wx-bar .wx-grow { display: none; }
  .wx-subnav { width: 100%; flex-wrap: nowrap; overflow-x: auto; order: 5; }
  .wx-logtime span { display: none; }
  .wx-logtime { margin-left: auto; width: 40px; padding: 0; justify-content: center; }
}

@media print {
  .wx-shell { display: block; padding: 0; }
  .wx-main { box-shadow: none; background: #fff; padding: 0 !important; }
  .wx-bar { display: none; }
}
</style>
